import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { toast } from 'react-toastify';

// COMPONENTS
import KogModalFadeComponent from '@/main/components/modal/ModalFadeComponent';
import SecondaryButton from '@/main/components/shared/button/secondaryButton';
import Button from '@/main/components/shared/button/button';

// UTILS/SERVICES/MODELS
import { CartState } from '@/main/store/cart';
import { quoteApi } from '@/main/api';
import {
  getDefaultCountryOptionDrop,
  getTranslateByKey,
  isNullUndefined,
  isNullUndefinedEmpty
} from '@/main/services/utils';
import { getGuidsQuoteItemInCart } from '@/main/store/cart/cartUtils';

// STYLES & COMPONENTS LOCAL
import { RefreshQuoteByGetItemsType, TypeBoxCart } from '../types';
import {
  ReasonFilterRequest,
  RequoteQuoteItemRequest,
  RequoteRequest
} from '@/main/models/request';
import { KeyValueDefault } from '@/main/models/common';
import * as S from './styles';
import KogAutoCompleteControlled from '../../../inputAutoComplete/autoCompleteControlled';
import { IRequoterequest } from '../modalRequoteParts/modalRequoteParts';
import { useForm } from 'react-hook-form';
import { convertStringForBoolean } from '@/main/services/utilsString';
import { getReason, useReasonReducer } from '@/main/store/reason';
import KogInputTextControlledNew from '../../../inputText/inputTextControlledNew';
import { ReasonTypeEnum } from '@/main/services/enum/ReasonEnum';
import { getCountriesUser, useCountryReducer } from '@/main/store/country';
import { useLocation, useNavigate } from 'react-router-dom';
import { RoutesConstants } from '@/main/routes/routesConstants';

type TypeModalConfirmRequote =
  | TypeBoxCart
  | 'unasweredBulkRequote'
  | 'noSupplierAssignedBulkRequote';

interface IModalConfirmRequotePartProps {
  partSelected: RequoteQuoteItemRequest[];
  getDeadlineAnswer?: () => number | undefined;
  openModalConfirm: boolean;
  type?: TypeModalConfirmRequote;
  isByModal?: boolean;
  dataCart: CartState;
  isRequotePartCanceled?: boolean;
  setOpenModalConfirm: React.Dispatch<React.SetStateAction<boolean>>;
  callbackAfterRemovePart?: (hasLastPartInCart: boolean) => void;
  reason?: KeyValueDefault | null;
  reasonDescription?: string | null;
  refreshQuoteByGetItems: RefreshQuoteByGetItemsType;
  hasReason?: boolean;
  callbackAfterRequotePart?: () => void;
}
function ModalConfirmRequotePart({
  partSelected,
  openModalConfirm,
  isByModal = false,
  type = 'unaswered',
  dataCart,
  isRequotePartCanceled,
  getDeadlineAnswer = () => {
    return undefined;
  },
  setOpenModalConfirm,
  callbackAfterRemovePart,
  callbackAfterRequotePart,
  reason,
  reasonDescription = '',
  refreshQuoteByGetItems,
  hasReason
}: IModalConfirmRequotePartProps) {
  const [isLoadingSave, setIsLoadingSave] = useState<boolean>(false);
  const navigate = useNavigate();
  const guidsCart = getGuidsQuoteItemInCart(dataCart);
  const guidsQuoteItem = partSelected.map(item => item.guidQuoteItem);
  const location = useLocation();

  const removeGuidsQuoteItem = guidsCart.filter(
    guid => !guidsQuoteItem.includes(guid)
  );
  const hasLastPartInCart = removeGuidsQuoteItem.length === 0;

  const INITIAL_FORM_STATE: IRequoterequest = {
    deadlineAnswer: 0,
    parts: [],
    reason: null as KeyValueDefault | null,
    reasonDescription: ''
  };

  const methods = useForm<IRequoterequest>({
    mode: 'onChange',
    defaultValues: INITIAL_FORM_STATE
  });

  const { reason: reasonReducer } = useReasonReducer();
  const { countriesUser: countryReducer } = useCountryReducer();

  const { control, setValue, watch, getValues } = methods;
  const dispatch = useDispatch();

  const watcherReasonType = watch('reason');
  const isReasonOther = convertStringForBoolean(watcherReasonType?.default);

  function validateForm() {
    const data = getValues();
    if (isNullUndefined(data.reason)) {
      toast.error(
        getTranslateByKey('information.selectOne', true).replace(
          '{0}',
          getTranslateByKey('information.reason').toLowerCase()
        )
      );
      return false;
    }

    if (isReasonOther && isNullUndefinedEmpty(data.reasonDescription)) {
      toast.error(getTranslateByKey('information.descriptionRequired', true));
      return false;
    }

    return true;
  }

  async function onClickActionConfirm() {
    try {

      const request: RequoteRequest = {
        quoteItems: partSelected,
        deadlineAnswerRequote: getDeadlineAnswer(),
        guidReason: String(reason?.key),
        reasonDescription: String(reasonDescription)
      };

      if (hasReason) {
        if (!validateForm()) {
          return;
        }
        const data = getValues();
        request.guidReason = String(data.reason?.key);
        request.reasonDescription = data.reasonDescription;
      }

      setIsLoadingSave(true);

      const {
        data: { result }
      } = await quoteApi.requoteItems(request);

    } catch (error) {
      console.error(error);
    } finally {
      setIsLoadingSave(false);
      setOpenModalConfirm(false);

      if (!isNullUndefined(callbackAfterRemovePart)) {
        callbackAfterRemovePart(hasLastPartInCart);
      }
      if (!isNullUndefined(callbackAfterRequotePart)) {
        callbackAfterRequotePart();
      }
    }
  }

  const renderMessageConfirmationRequote = () => {
    const messageLastPartObj: Record<
      TypeModalConfirmRequote,
      { title: string; obs: string }
    > = {
      unaswered: {
        title: getTranslateByKey('information.confirmRequotePartCancelQuote'),
        obs: getTranslateByKey('information.obsConfirmRequotePartCancelQuote')
      },
      noSupplierAssigned: {
        title: getTranslateByKey('information.confirmRequotePartCancelQuote'),
        obs: getTranslateByKey('information.obsConfirmRequotePartCancelQuote')
      },
      assigned: {
        title: getTranslateByKey('information.confirmRequotePartCancelQuote'),
        obs: getTranslateByKey('information.obsConfirmRequotePartCancelQuote')
      },
      noSupplierAssignedBulkRequote: {
        title: getTranslateByKey(
          'information.confirmRequotePartCancelQuoteBulk'
        ),
        obs: getTranslateByKey(
          'information.obsConfirmRequotePartCancelQuoteBulk'
        )
      },
      unasweredBulkRequote: {
        title: getTranslateByKey(
          'information.confirmRequotePartCancelQuoteBulk'
        ),
        obs: getTranslateByKey(
          'information.obsConfirmRequotePartCancelQuoteBulk'
        )
      }
    };

    const messageRequotePartObj: Record<
      TypeModalConfirmRequote,
      { title: string; obs: string }
    > = {
      unaswered: {
        title: getTranslateByKey('information.confirmRequotePart'),
        obs: getTranslateByKey('information.obsConfirmRequotePart')
      },
      noSupplierAssigned: {
        title: getTranslateByKey('information.confirmRequotePart'),
        obs: getTranslateByKey('information.obsConfirmRequotePart')
      },
      assigned: {
        title: getTranslateByKey('information.confirmRequotePart'),
        obs: getTranslateByKey('information.obsConfirmRequotePart')
      },
      noSupplierAssignedBulkRequote: {
        title: getTranslateByKey('information.confirmRequotePartBulk'),
        obs: getTranslateByKey('information.obsConfirmRequotePartBulk')
      },
      unasweredBulkRequote: {
        title: getTranslateByKey('information.confirmRequotePartBulk'),
        obs: getTranslateByKey('information.obsConfirmRequotePartBulk')
      }
    };

    if (hasLastPartInCart && !isRequotePartCanceled)
      return (
        <S.AreaMessageConfirmation>
          <p>{messageLastPartObj[type].title}</p>
          <p>{messageLastPartObj[type].obs}</p>
        </S.AreaMessageConfirmation>
      );
    return (
      <S.AreaMessageConfirmation>
        <p>{messageRequotePartObj[type].title}</p>
        <p>{messageLastPartObj[type].obs}</p>
      </S.AreaMessageConfirmation>
    );
  };

  useEffect(() => {
    const countryUser = getDefaultCountryOptionDrop(countryReducer.data);
    if (countryUser === null) return;

    const requestReason: ReasonFilterRequest = {
      guidCountry: String(countryUser.key),
      idReasonType: ReasonTypeEnum.RequoteQuote
    };
    dispatch(getReason(requestReason));
  }, [JSON.stringify(countryReducer.data)]);


  useEffect(() => {
    dispatch(getCountriesUser());
  }, []);


  return (
    <S.ContainerModalConfirmation>
      <h6>{renderMessageConfirmationRequote()}</h6>
      {hasReason && (
        <S.AreaReason>
          <KogAutoCompleteControlled
            id="id-requote-parts-quote-select-reason"
            nameCustom="reason"
            isDefaultValueUnique
            label={getTranslateByKey('information.reason')}
            control={control}
            rulesCustom={{ required: true }}
            setValueCustom={setValue}
            options={reasonReducer.data}
            isLoading={reasonReducer.isLoading}
            onChangeCustom={option => {
              if (option === null) return;

              if (
                convertStringForBoolean((option as KeyValueDefault)?.default)
              ) {
                setValue('reasonDescription', '');
              }
            }}
          />
          {isReasonOther && (
            <KogInputTextControlledNew
              required={isReasonOther}
              id="id-requote-quote-input-description"
              label={getTranslateByKey('information.description')}
              name="reasonDescription"
              control={control}
            />
          )}
        </S.AreaReason>
      )}
      <div
        style={{
          display: 'flex',
          justifyContent: 'center',
          gap: "1rem",
        }}>
        <SecondaryButton
          type="button"
          disabled={isLoadingSave}
          onClick={() => {
            setOpenModalConfirm(false);
          }}
          id="id-createOrder-button-no">
          {getTranslateByKey('information.no')}
        </SecondaryButton>
        <Button
          id="id-createOrder-button-yes"
          type="button"
          isLoading={isLoadingSave}
          onClick={() => onClickActionConfirm()}>
          {getTranslateByKey('information.yes')}
        </Button>
      </div>
    </S.ContainerModalConfirmation>
  );
}
function WrapperModalConfirmRequote(props: IModalConfirmRequotePartProps) {
  return (
    <KogModalFadeComponent
      isInModal
      closeByOverlay={false}
      open={props.openModalConfirm}
      setOpen={props.setOpenModalConfirm}>
      {props.openModalConfirm ? (
        <ModalConfirmRequotePart {...props} />
      ) : (
        <div />
      )}
    </KogModalFadeComponent>
  );
}
export default WrapperModalConfirmRequote;

